import { groupBy } from "lodash"
import type { SpecialPricingReportRequestData, VoidsLogReportItem } from "../../../../graphql/types"
import { firstLetterUpperCase, formatDateTime } from "../../../../utils/formatters"
import { GLOBAL_FILTER_IDENTIFIER } from "../../../filters/reducer"
import {
    getFilters,
    getSelectedLocationIdsFromGlobalFilter,
    getTimezoneForQueryParam,
} from "../../../filters/selectors"
import type { StateObject } from "../../../redux/types"
import { getReportsStateObject } from "../selectors"
import type { VoidsReportTableItem } from "./VoidsSummaryReportTable"

export const getVoidsReportQueryApiParam = (state: StateObject): SpecialPricingReportRequestData => {
    const globalFilters = getFilters(state, GLOBAL_FILTER_IDENTIFIER)
    const selectedLocationIds = getSelectedLocationIdsFromGlobalFilter(state)
    const locationIdsParam = selectedLocationIds?.join() ?? null
    const from =
        globalFilters.timestamp &&
        globalFilters.timestamp.from &&
        formatDateTime(globalFilters.timestamp.from, "YYYY-MM-DD")
    const to =
        globalFilters.timestamp &&
        globalFilters.timestamp.to &&
        formatDateTime(globalFilters.timestamp.to, "YYYY-MM-DD")
    return {
        from: from,
        to: to,
        timezone: getTimezoneForQueryParam(state, selectedLocationIds),
        ...(locationIdsParam
            ? {
                  location_ids: locationIdsParam,
              }
            : null),
    }
}

export const getDataForVoidsLogsTable = (state: StateObject): VoidsLogReportItem[] => {
    const data = getReportsStateObject(state).voidsLogs

    if (!data || data.length === 0) {
        return []
    }

    const finalTotal = data.reduce((acc: number, val: VoidsLogReportItem) => acc + val.amount, 0)

    return [
        ...data,
        {
            id: "total",
            // @ts-expect-error
            name: "Total",
            amount: finalTotal,
        },
    ]
}

export const getDataForVoidsSummaryTable = (state: StateObject): VoidsReportTableItem[] => {
    const data = getReportsStateObject(state).voidsSummary
    const groupedByType = groupBy(data, "type")
    const res = Object.keys(groupedByType).map((key, index) => {
        return {
            id: `${index}`,
            level: 0,
            name: firstLetterUpperCase(key),
            count: groupedByType[key].reduce((acc: number, val) => acc + (val.quantity || 0), 0),
            total: groupedByType[key].reduce((acc, val) => acc + (val.amount || 0), 0),
            children: groupedByType[key].map(ch => ({
                id: ch.row_number,
                name: ch.item_name || ch.reason_code,
                count: ch.quantity,
                total: ch.amount,
            })),
        }
    })

    if (!res || res.length === 0) {
        return []
    }

    const finalCount = res.reduce((acc: number, val: VoidsReportTableItem) => acc + (val.count || 0), 0)
    const finalTotal = res.reduce((acc: number, val: VoidsReportTableItem) => acc + (val.total || 0), 0)
    return [
        ...res,
        {
            id: "total",
            name: "Total",
            total: finalTotal,
            count: finalCount,
            level: 0,
            children: null,
        },
    ]
}
